import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import { GraphqlQueryContext } from '@interfaces/graphql';
import {
  BioinformaticReportByCategoryQueryResult,
  BioinformaticReportQueriesFindByCategoryArgs,
  DnaReportTypeQueriesFindAllArgs,
  DnaReportTypeQueryResult,
  Query
} from '@interfaces/schema';
import { WebStorageReportOptions } from '@interfaces/web-storage';
import { Session } from '@providers/session/session.service';

/**
 * Report View Service.
 *
 * @exports
 * @class ReportViewService
 */
@Injectable()
export class ReportViewService {
  private readonly apollo = inject(Apollo);
  private readonly session = inject(Session);

  /**
   * Get context.
   *
   * @returns {GraphqlQueryContext}
   * @memberof ReportViewService
   */
  get context(): GraphqlQueryContext {
    return {
      contentLanguage: this.language
    };
  }

  /**
   * Get active or selected language.
   *
   * @returns {string}
   */
  get language(): string | undefined {
    return this.options?.language;
  }

  /**
   * Get option report.
   *
   * @returns {WebStorageReportOptions}
   */
  get options(): WebStorageReportOptions | null {
    return this.session.retrieve(this.session.keys.reportNourishOptions) ?? {};
  }

  /**
   * FindAllCategory.
   *
   * @param {BioinformaticReportQueriesFindByCategoryArgs} variables BioinformaticReportQueriesFindByCategoryArgs.
   * @returns {Observable<BioinformaticReportByCategoryQueryResult>}
   * @memberof ReportViewService
   */
  findAllCategory(
    variables: BioinformaticReportQueriesFindByCategoryArgs
  ): Observable<BioinformaticReportByCategoryQueryResult> {
    const query = gql`
      query findAllCategory(
        $where: BioinformaticReportByCategoryFilter
        $sort: BioinformaticReportByCategorySortBy
      ) {
        Bioinformatic {
          Report {
            findByCategory(where: $where, sort: $sort) {
              count
              results {
                id
                name
                thumbnail
                dnaCategory {
                  id
                  name
                  description
                  shortDescription
                  objectImage
                  infoImageAsset
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables,
        context: this.context
      })
      .pipe(
        map(
          ({ data }) =>
            data?.Bioinformatic?.Report
              ?.findByCategory as BioinformaticReportByCategoryQueryResult
        )
      );
  }

  /**
   * FindAllCategory.
   *
   * @param {DnaReportTypeQueriesFindAllArgs} variables DnaReportTypeQueriesFindAllArgs.
   * @returns {Observable<DnaReportTypeQueryResult>}
   * @memberof ReportViewService
   */
  findReportTypeByCode(
    variables: DnaReportTypeQueriesFindAllArgs
  ): Observable<DnaReportTypeQueryResult> {
    const query = gql`
      query ReportType($where: DnaReportTypeQueryFilter, $limit: Int) {
        DNA {
          Report {
            Type {
              findAll(where: $where, limit: $limit) {
                results {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables
      })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.Report?.Type?.findAll as DnaReportTypeQueryResult
        )
      );
  }
}
